import { Injectable, inject } from '@angular/core';
import { AssetsService } from '@api';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { forkJoin, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { tryParseJSON } from 'src/app/shared/extensions/string.extensions';
import { ICapabilityModelDefinition } from 'src/app/shared/interfaces/capability-model-definition';
import { ApplicationState } from 'src/app/shared/state/application.state';
import { SetCapabilityModel, SetCapabilityModelUi } from '../asset-capability-state/asset-capability.action';
import { ResetAssetDetails } from '../asset-details.actions';
import * as actions from './asset-selected.actions';
import { AssetSelectedStateModel, assetSelectedDefaults } from './asset-selected.state.model';

@State<AssetSelectedStateModel>(assetSelectedDefaults)
@Injectable()
export class AssetSelectedState {
    private assetsService = inject(AssetsService);
    private store = inject(Store);

    @Selector()
    public static getSelectedDeviceId(state: AssetSelectedStateModel) {
        return state.selectedEdgeDeviceId;
    }

    @Selector()
    public static getSelectedAssetId(state: AssetSelectedStateModel) {
        return state.selectedAssetId;
    }

    @Selector()
    public static getIsAssetOffline(state: AssetSelectedStateModel) {
        return state.asset?.deviceStatus != null && !state.asset.deviceStatus;
    }

    @Selector()
    public static getAsset(state: AssetSelectedStateModel) {
        return state.asset;
    }

    @Selector()
    public static getAssetId(state: AssetSelectedStateModel) {
        return state.asset?.id;
    }

    @Selector()
    public static getIsAssetNotFound(state: AssetSelectedStateModel) {
        return state.assetNotFound;
    }

    @Selector()
    public static getIsAssetNotConnected(state: AssetSelectedStateModel) {
        return state.assetNotConnected;
    }

    @Selector()
    public static getIfShowAssetDetail(state: AssetSelectedStateModel) {
        return !(state.assetNotConnected || state.assetNotFound);
    }

    @Action(actions.LoadAssetDetails)
    public loadAssetDetails({ patchState }: StateContext<AssetSelectedStateModel>, action: actions.LoadAssetDetails) {
        patchState({
            selectedAssetId: action.assetId,
            selectedEdgeDeviceId: action.edgeDeviceId,
        });

        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);
        return this.assetsService.getAsset(action.edgeDeviceId, action.assetId, tenantName, false, false).pipe(
            tap((result) => {
                patchState({
                    asset: result,
                    selectedAssetId: action.assetId,
                    selectedEdgeDeviceId: action.edgeDeviceId,
                    assetNotConnected: false,
                    assetNotFound: false,
                });
            }),
            catchError((err) => {
                if (err.status === 406) {
                    patchState({
                        assetNotConnected: true,
                    });
                } else if (err.status === 404) {
                    patchState({
                        assetNotFound: true,
                    });
                }
                return of();
            })
        );
    }

    @Action(actions.LoadAssetMetadata)
    public loadAssetMetadata({ patchState }: StateContext<AssetSelectedStateModel>, action: actions.LoadAssetMetadata) {
        patchState({
            selectedAssetId: action.assetId,
            selectedEdgeDeviceId: action.edgeDeviceId,
        });

        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);
        return this.assetsService.getAssetMetadata(action.edgeDeviceId, action.assetId, tenantName).pipe(
            tap((result) => {
                patchState({
                    asset: result,
                    selectedAssetId: action.assetId,
                    selectedEdgeDeviceId: action.edgeDeviceId,
                    assetNotConnected: false,
                    assetNotFound: false,
                });
            }),
            catchError((err) => {
                if (err.status === 406) {
                    patchState({
                        assetNotConnected: true,
                    });
                } else if (err.status === 404) {
                    patchState({
                        assetNotFound: true,
                    });
                }
                return of();
            })
        );
    }

    @Action(actions.LoadAssetCapabilityModelUserInterface)
    public loadAssetCapabilityModelUserInterface({ getState, patchState, dispatch }: StateContext<AssetSelectedStateModel>, action: actions.LoadAssetCapabilityModelUserInterface) {
        const state = getState();
        if (state.capabilityModelUi != null) {
            dispatch(new SetCapabilityModelUi(state.capabilityModelUi));
            return of(state.capabilityModelUi);
        }

        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);
        return this.assetsService.getAssetUserInterface(action.edgeDeviceId, action.assetId, tenantName).pipe(
            tap((result) => {
                const model = tryParseJSON(result.model);

                patchState({
                    capabilityModelUi: model,
                });

                dispatch(new SetCapabilityModelUi(model));
            })
        );
    }

    @Action(actions.LoadAssetCapabilityModel)
    public loadAssetCapabilityModel({ patchState, dispatch }: StateContext<AssetSelectedStateModel>, action: actions.LoadAssetCapabilityModel) {
        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);
        const getAssetCapabilityModel$ = this.assetsService.getAssetCapabilityModel(action.edgeDeviceId, action.assetId, tenantName);
        const getCustomerDefinitions$ = this.assetsService.getCustomerDefinitions(action.edgeDeviceId, action.assetId, tenantName);

        return forkJoin([getAssetCapabilityModel$, getCustomerDefinitions$]).pipe(
            tap(([dcm, customerDefinitions]) => {
                const capabilityModelDefinition: ICapabilityModelDefinition = { id: dcm?.id, model: dcm?.model, customerDefinitions };
                patchState({
                    capabilityModelDefinition,
                });

                dispatch(new SetCapabilityModel(capabilityModelDefinition));
            })
        );
    }

    @Action(actions.PushAssetDeviceStatus)
    public pushDeviceStatus({ setState }: StateContext<AssetSelectedStateModel>, action: actions.PushAssetDeviceStatus) {
        const deviceStatus = action.data.deviceStatus;
        setState(
            patch({
                asset: patch({ deviceStatus }),
            })
        );
    }

    @Action(actions.SetAssetMetadata)
    public setAssetMetadata(ctx: StateContext<AssetSelectedStateModel>, action: actions.SetAssetMetadata) {
        const state = ctx.getState();

        if (action.assetMetadata.isActivated === null) {
            action.assetMetadata.isActivated = state.asset.isActivated;
        }

        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);

        return this.assetsService.editAssetMetadata(state.selectedEdgeDeviceId, state.selectedAssetId, tenantName, action.assetMetadata);
    }

    @Action(ResetAssetDetails)
    public resetState({ patchState }: StateContext<AssetSelectedStateModel>) {
        patchState({
            capabilityModelDefinition: null,
            capabilityModelUi: null,
        });
    }
}
